import RevealStagger from "@/components/common/RevealStagger";
import Button from "@/components/common/Button";
import { ArrowRight } from "@/components/common/icons";
import { STEPS } from "@/lib/data";
import { useLocale } from "@/i18n/useLocale";
import { landingStepsFor } from "@/content/landing";
import { pathFor } from "@/routes/manifest";

export default function StepsPreview(): JSX.Element {
  const locale = useLocale();
  const t = landingStepsFor(locale);

  return (
    <section className="section-y-lg bg-bg-2 border-y border-border relative overflow-hidden">
      <div
        className="aura-bg"
        aria-hidden="true"
        style={{ ["--aura-size" as string]: "400px", ["--aura-top" as string]: "-100px", ["--aura-opacity" as string]: "0.3" }}
      />
      <div className="container-page relative z-[2]">
        <div className="flex justify-between items-end gap-6 flex-wrap mb-8 md:mb-10">
          <div>
            <h2
              className="font-display font-bold"
              style={{ fontSize: "clamp(28px, 3vw, 48px)", lineHeight: 1, letterSpacing: "-0.025em" }}
            >
              {t.title.lead}
              <span className="text-brand-gradient">{t.title.highlight}</span>
            </h2>
          </div>
          <Button variant="ghost" href={pathFor("/how-it-works", locale)}>
            {t.ctaFullGuide} <ArrowRight />
          </Button>
        </div>

        <RevealStagger className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4" step={120}>
          {STEPS.map((s) => (
            <div key={s.key} className="glass-card glass-card-hover h-full flex flex-col p-6 relative" style={{ minHeight: 320 }}>
              {/*
               * Ghost step number — Archivo Black, brand-tinted
               * 12% alpha, top-right. Sits behind the content
               * via lower z-index (the parent's overflow-hidden
               * keeps it inside the card).
               */}
              <span
                aria-hidden="true"
                className="absolute font-display font-bold leading-[0.85] tracking-tight pointer-events-none"
                style={{
                  fontFamily: '"Archivo Black", sans-serif',
                  fontSize: 80,
                  letterSpacing: "-0.04em",
                  right: 18,
                  top: 14,
                  color: "rgb(var(--brand) / 0.14)",
                }}
              >
                {s.n}
              </span>

              <div className="font-mono text-[10px] text-brand-emphasis tracking-[0.18em] font-bold relative">
                {t.stepLabel} {s.n}
              </div>
              <h3
                className="font-display font-semibold mt-3.5 relative"
                style={{ fontSize: 22, letterSpacing: "-0.015em" }}
              >
                {t.steps[s.key].title}
              </h3>
              <p className="text-fg-muted text-[13px] leading-[1.55] mt-3 relative">{t.steps[s.key].body}</p>

              <div className="mt-auto pt-3.5 border-t border-border flex flex-col gap-1.5 relative">
                {t.steps[s.key].bullets.slice(0, 2).map((b) => (
                  <div key={b} className="text-fg-muted flex gap-1.5" style={{ fontSize: 11 }}>
                    <span className="text-brand-emphasis">→</span>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </RevealStagger>
      </div>
    </section>
  );
}
